import { User } from 'src/app/shared/models/user.model';

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { AutomatedForms, CWSForm, RTADetails, RemoveRTAFiles,CWSRequest, ControlPlanDetails, DimensionalInspectionDetails, SolarCharacteristicsReportDetails, RTAFeatureFlags, MaterialProcessInspectionDetails } from 'src/app/shared/models/solarForms.model';
import { DataSourceService } from '../data-source.service';
import { HttpParams, HttpResponse } from '@angular/common/http';
@Injectable({
  providedIn: 'root'
})
export class SolarFormsRepositoryService {
  // Fetched once per app load; the API reads it live from appsettings (RTA section).
  private rtaFeatureFlags$: Observable<RTAFeatureFlags>;

  constructor(private dataSource: DataSourceService) {

  }

  getRTAFeatureFlags(): Observable<RTAFeatureFlags> {
    if (!this.rtaFeatureFlags$) {
      this.rtaFeatureFlags$ = this.dataSource.getRTAFeatureFlags().pipe(
        catchError(() => of({ allowAdminSubmit: false } as RTAFeatureFlags)),
        shareReplay(1)
      );
    }
    return this.rtaFeatureFlags$;
  }
  getAutomatedFormRequests(id: string, cwsFormOption:number): Observable<AutomatedForms[]> {
    const params = new HttpParams()
      .set('id', id)
      .set('cwsFormOption', cwsFormOption)
    return this.dataSource.getAutomatedFormRequests(id,cwsFormOption);
  }

  updateCwsForm(data: CWSForm): Observable<any> {
    return this.dataSource.updateCwsFormData(data);
  }
  postCwsForm(data: CWSForm): Observable<any> {
    return this.dataSource.postCwsFormData(data);
  }
  getAllSupplierUserAssociatedWithBuyer(cwsIdNo:string, supplierId: string): Observable<any> {
    return this.dataSource.getAllSupplierUserAssociatedWithBuyer(cwsIdNo, supplierId)
  }
  getAllAssociatedBuyersWithSupplier(cwsIdNo:string): Observable<any> {
    return this.dataSource.getAllAssociatedBuyersWithSupplier(cwsIdNo)
  }
  getBuyersBySupplierId(cwsId: string, supplierId: string): Observable<any> {
    return this.dataSource.getBuyersBySupplierId(cwsId, supplierId)
  }
  getSuppliersByBuyerId(cwsId : string): Observable<any> {
    return this.dataSource.getSuppliersByBuyerId(cwsId);
  }
  getAutomatedFormRequestById(requestId : string): Observable<any> {
    return this.dataSource.getAutomatedFormRequestById(requestId);
  }
  getAutomatedRequestFormByCwsNumber(data : any, tokenData:any): Observable<CWSForm> {
    return this.dataSource.getAutomatedRequestFormByCwsNumber(data, tokenData);
  }

  updateExternalFormData(data: any, token, expires): Observable<any> {
    return this.dataSource.updateExternalFormData(data, token, expires);
  }

  resendCWSNotification(formId: string): Observable<any> {
    return this.dataSource.resendCWSNotification(formId);
  }

  getPPAPReadOnlyDetails(data: string): Observable<any> {
    return this.dataSource.getPPAPReadOnlyDetails(data);
  }

  submitPPAPForm(data): Observable<any> {
    return this.dataSource.submitPPAPForm(data);
  }
  savePPAPForm(data): Observable<any> {
    return this.dataSource.savePPAPForm(data);
  }

  uploadRTAContent(data:FormData): Observable<any> {
    return this.dataSource.uploadRTAContent(data);
  }

  saveRTAFormInfo(RTAFormInfo: RTADetails): Observable<any> {
    return this.dataSource.saveRTAFormDetails(RTAFormInfo);
  }

  updateRTAFormDetails(RTAFormInfo: RTADetails): Observable<any> {
    return this.dataSource.updateRTAFormDetails(RTAFormInfo);
  }

  submitRTAFormDetails(RTAFormInfo: RTADetails): Observable<any> {
    return this.dataSource.submitRTAFormDetails(RTAFormInfo);
  }

  // TEMP (manual verification): passthrough for the Section 1B overflow .docx
  // preview download. Remove later.
  testGenerateSection1BDoc(RTAFormInfo: RTADetails): Observable<HttpResponse<Blob>> {
    return this.dataSource.testGenerateSection1BDoc(RTAFormInfo);
  }

  getRTALookupByDomain(domain: string): Observable<any> {
    return this.dataSource.getRTALookupByDomain(domain);
  }

  getRTAFormDetailsById(requestId : string): Observable<any> {
    return this.dataSource.getRTAFormDetailsById(requestId);
  }

  // PBI 314702 — resolves RTA details + edit mode for the row-click flow.
  getRTAForEdit(rtaNumber: string | null, localId: string | null): Observable<any> {
    return this.dataSource.getRTAForEdit(rtaNumber, localId);
  }

  removeRTAFiles(removeRTAFile: RemoveRTAFiles): Observable<RemoveRTAFiles> {
    return this.dataSource.removeRTAFiles(removeRTAFile);
  }

  saveControlPlanFormDetails(controlPlanDetails: ControlPlanDetails): Observable<ControlPlanDetails> {
    return this.dataSource.saveControlPlanFormDetails(controlPlanDetails);
  }

  updateControlPlanFormDetails(controlPlanDetails: ControlPlanDetails): Observable<ControlPlanDetails> {
    return this.dataSource.updateControlPlanFormDetails(controlPlanDetails);
  }

  getControlPlanFormDetails(requestNumber: string): Observable<ControlPlanDetails> {
    return this.dataSource.getControlPlanFormDetails(requestNumber);
  }

  submitControlPlanFormDetails(requestNumber: string): Observable<any> {
    return this.dataSource.submitControlPlanFormDetails(requestNumber);
  }

  exportControlPlanPdf(requestNumber: string): Observable<Blob> {
    return this.dataSource.exportControlPlanPdf(requestNumber);
  }

  exportControlPlanWord(requestNumber: string): Observable<Blob> {
    return this.dataSource.exportControlPlanWord(requestNumber);
  }

  saveDimensionalInspectionDetails(detail: DimensionalInspectionDetails): Observable<DimensionalInspectionDetails> {
    return this.dataSource.saveDimensionalInspectionDetails(detail);
  }

  updateDimensionalInspectionDetails(detail: DimensionalInspectionDetails): Observable<DimensionalInspectionDetails> {
    return this.dataSource.updateDimensionalInspectionDetails(detail);
  }

  getDimensionalInspectionDetails(requestNumber: string): Observable<DimensionalInspectionDetails> {
    return this.dataSource.getDimensionalInspectionDetails(requestNumber);
  }

  submitDimensionalInspectionDetails(requestNumber: string): Observable<any> {
    return this.dataSource.submitDimensionalInspectionDetails(requestNumber);
  }

  exportDimensionalInspectionPdf(requestNumber: string): Observable<Blob> {
    return this.dataSource.exportDimensionalInspectionPdf(requestNumber);
  }

  exportDimensionalInspectionWord(requestNumber: string): Observable<Blob> {
    return this.dataSource.exportDimensionalInspectionWord(requestNumber);
  }

  saveMaterialProcessInspectionDetails(detail: MaterialProcessInspectionDetails): Observable<MaterialProcessInspectionDetails> {
    return this.dataSource.saveMaterialProcessInspectionDetails(detail);
  }

  updateMaterialProcessInspectionDetails(detail: MaterialProcessInspectionDetails): Observable<MaterialProcessInspectionDetails> {
    return this.dataSource.updateMaterialProcessInspectionDetails(detail);
  }

  getMaterialProcessInspectionDetails(requestNumber: string): Observable<MaterialProcessInspectionDetails> {
    return this.dataSource.getMaterialProcessInspectionDetails(requestNumber);
  }

  submitMaterialProcessInspectionDetails(requestNumber: string): Observable<any> {
    return this.dataSource.submitMaterialProcessInspectionDetails(requestNumber);
  }

  exportMaterialProcessInspectionPdf(requestNumber: string): Observable<Blob> {
    return this.dataSource.exportMaterialProcessInspectionPdf(requestNumber);
  }

  exportMaterialProcessInspectionWord(requestNumber: string): Observable<Blob> {
    return this.dataSource.exportMaterialProcessInspectionWord(requestNumber);
  }

  saveSolarCharacteristicsReportDetails(detail: SolarCharacteristicsReportDetails): Observable<SolarCharacteristicsReportDetails> {
    return this.dataSource.saveSolarCharacteristicsReportDetails(detail);
  }

  updateSolarCharacteristicsReportDetails(detail: SolarCharacteristicsReportDetails): Observable<SolarCharacteristicsReportDetails> {
    return this.dataSource.updateSolarCharacteristicsReportDetails(detail);
  }

  getSolarCharacteristicsReportDetails(requestNumber: string): Observable<SolarCharacteristicsReportDetails> {
    return this.dataSource.getSolarCharacteristicsReportDetails(requestNumber);
  }

  submitSolarCharacteristicsReportDetails(requestNumber: string): Observable<any> {
    return this.dataSource.submitSolarCharacteristicsReportDetails(requestNumber);
  }

  exportSolarCharacteristicsReportPdf(requestNumber: string): Observable<Blob> {
    return this.dataSource.exportSolarCharacteristicsReportPdf(requestNumber);
  }

  exportSolarCharacteristicsReportWord(requestNumber: string): Observable<Blob> {
    return this.dataSource.exportSolarCharacteristicsReportWord(requestNumber);
  }
  
}
